import React, { useEffect, useState, useRef, Component } from 'react';
// --- FadeIn (Scroll Entry Animation) ---
export function FadeIn({
  children,
  className = '',
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (domRef.current) observer.unobserve(domRef.current);
        }
      });
    }, {
      rootMargin: '0px 0px -10% 0px'
    });
    if (domRef.current) observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);
  const delayClass = delay === 100 ? 'delay-100' : delay === 200 ? 'delay-200' : delay === 300 ? 'delay-300' : delay === 400 ? 'delay-400' : '';
  return <div ref={domRef} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${delayClass} ${className}`} data-id="element-349">
      {children}
    </div>;
}
// --- Button ---
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'nav';
  href?: string;
  as?: 'button' | 'a';
};
export function Button({
  variant = 'primary',
  className = '',
  children,
  href,
  as,
  ...props
}: ButtonProps) {
  const baseStyles = 'inline-flex items-center justify-center transition-editorial duration-600 font-medium tracking-wide text-sm';
  const variants = {
    primary: "bg-atrium-green text-atrium-ivory px-8 py-4 rounded-md hover:bg-opacity-90 relative after:content-[''] after:absolute after:bottom-2 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[1px] after:bg-atrium-bronze hover:after:w-1/2 after:transition-all after:duration-600",
    secondary: 'border border-atrium-ink text-atrium-ink px-8 py-4 rounded-md hover:bg-atrium-ink hover:text-atrium-ivory',
    tertiary: "text-atrium-ink hover:text-atrium-green relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-full after:h-[1px] after:bg-atrium-bronze after:origin-right hover:after:origin-left hover:after:scale-x-0 after:transition-transform after:duration-600",
    nav: 'bg-atrium-green text-atrium-ivory px-6 py-3 rounded-md hover:bg-opacity-90'
  };
  const Component = as || (href ? 'a' : 'button');
  const combinedClassName = `${baseStyles} ${variants[variant]} ${className}`;
  if (Component === 'a') {
    return <a href={href} className={combinedClassName} {...props as any} data-id="element-350">
        {children}
      </a>;
  }
  return <button className={combinedClassName} {...props} data-id="element-351">
      {children}
    </button>;
}
// --- Corner Brackets Motif ---
export function CornerBrackets({
  color = 'bronze'
}: {
  color?: 'bronze' | 'green';
}) {
  const borderColor = color === 'bronze' ? 'border-atrium-bronze' : 'border-atrium-green';
  return <>
      <div className={`absolute top-0 left-0 w-4 h-4 border-t border-l ${borderColor} opacity-60 pointer-events-none`} data-id="element-352" />
      <div className={`absolute top-0 right-0 w-4 h-4 border-t border-r ${borderColor} opacity-60 pointer-events-none`} data-id="element-353" />
      <div className={`absolute bottom-0 left-0 w-4 h-4 border-b border-l ${borderColor} opacity-60 pointer-events-none`} data-id="element-354" />
      <div className={`absolute bottom-0 right-0 w-4 h-4 border-b border-r ${borderColor} opacity-60 pointer-events-none`} data-id="element-355" />
    </>;
}
// --- Atrium Line Motif ---
export function AtriumLine({
  vertical = false,
  className = '',
  color = 'bronze'
}: {
  vertical?: boolean;
  className?: string;
  color?: 'bronze' | 'green' | 'stone';
}) {
  const bgColor = color === 'bronze' ? 'bg-atrium-bronze' : color === 'green' ? 'bg-atrium-green' : 'bg-atrium-stone';
  const dimensions = vertical ? 'w-[1px] h-full' : 'h-[1px] w-full';
  return <div className={`${bgColor} ${dimensions} opacity-40 ${className}`} aria-hidden="true" data-id="element-356" />;
}
// --- Section Header ---
export function SectionHeader({
  eyebrow,
  title,
  className = '',
  light = false
}: {
  eyebrow: string;
  title: string;
  className?: string;
  light?: boolean;
}) {
  return <div className={`mb-12 md:mb-16 ${className}`} data-id="element-357">
      <span className={`block text-xs font-bold tracking-[0.15em] uppercase mb-4 ${light ? 'text-atrium-stone' : 'text-atrium-green'}`} data-id="element-358">
        {eyebrow}
      </span>
      <h2 className={`text-3xl md:text-5xl font-serif text-balance leading-tight ${light ? 'text-atrium-ivory' : 'text-atrium-ink'}`} data-id="element-359">
        {title}
      </h2>
    </div>;
}